/**
 * Collect mesh/texture paths for fetching MJCF-linked files relative to `xmlUrl`,
 * honoring MuJoCo <compiler meshdir> / <compiler texturedir> (same rules as libraryAssetLoader.js).
 *
 * Without meshdir, bare names like `forearm_0.obj` resolve next to the XML file and 404
 * when assets actually live under `assets/`.
 */

export function collectMjcfAssetRefsForFetch(xmlDoc, xmlUrl) {
  const xmlBaseUrl = new URL(xmlUrl, globalThis.location?.href || "http://localhost/").toString();
  const compiler = xmlDoc.querySelector("compiler");
  const meshdir = (compiler?.getAttribute("meshdir") || "assets")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  const texturedir = (compiler?.getAttribute("texturedir") || "assets")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

  /** @type {Map<string, Set<string>>} */
  const byResolvedUrl = new Map();

  const add = (fetchPath, ...extraAliases) => {
    const fp = String(fetchPath || "").trim();
    if (!fp) return;
    let resolvedUrl;
    let resolvedPath;
    try {
      resolvedUrl = new URL(fp, xmlBaseUrl).toString();
      resolvedPath = new URL(resolvedUrl).pathname;
    } catch {
      return;
    }

    const register = (targetUrl, targetPath, aliasesToRegister) => {
      if (!byResolvedUrl.has(targetUrl)) byResolvedUrl.set(targetUrl, new Set());
      const set = byResolvedUrl.get(targetUrl);
      const pathAlias = String(targetPath || "").replace(/^\/+/, "");
      if (pathAlias) set.add(pathAlias);
      aliasesToRegister.forEach((alias) => {
        const t = typeof alias === "string" ? alias.trim() : "";
        if (t) set.add(t);
      });
    };

    const aliases = [fp, ...extraAliases];
    register(resolvedUrl, resolvedPath, aliases);
  };

  xmlDoc.querySelectorAll("mesh").forEach((meshEl) => {
    const fileAttr = meshEl.getAttribute("file")?.trim();
    if (!fileAttr) return;
    let fetchPath = fileAttr;
    if (!/^https?:\/\//i.test(fileAttr) && meshdir && !fileAttr.startsWith(`${meshdir}/`)) {
      fetchPath = `${meshdir}/${fileAttr}`;
    }
    add(fetchPath, fileAttr);
  });

  xmlDoc.querySelectorAll("texture").forEach((texEl) => {
    const fileAttr = texEl.getAttribute("file")?.trim();
    if (!fileAttr) return;
    let fetchPath = fileAttr;
    if (!/^https?:\/\//i.test(fileAttr) && texturedir && !fileAttr.startsWith(`${texturedir}/`)) {
      fetchPath = `${texturedir}/${fileAttr}`;
    }
    add(fetchPath, fileAttr);
  });

  xmlDoc.querySelectorAll("hfield").forEach((hfieldEl) => {
    const fileAttr = hfieldEl.getAttribute("file")?.trim();
    if (!fileAttr) return;
    add(fileAttr);
  });

  return Array.from(byResolvedUrl.entries()).map(([resolvedUrl, aliasSet]) => ({
    resolvedUrl,
    resolvedPath: new URL(resolvedUrl).pathname,
    aliases: Array.from(aliasSet),
  }));
}

/**
 * Register one fetched blob under every alias the MJCF parser / sceneLoader resolver may use.
 */
export function registerMjcfFetchedAssetInFileMap(
  fileMap,
  { blob, resolvedUrl, resolvedPath, aliases }
) {
  const resolvedNoSlash = resolvedPath.replace(/^\/+/, "");
  const baseName = resolvedNoSlash.replace(/^.*\//, "");
  const preferredName =
    (Array.isArray(aliases) ? aliases.find((a) => a && !String(a).includes("/")) : null) ||
    baseName ||
    "asset";
  const file = new File([blob], preferredName, { type: blob.type || "application/octet-stream" });

  if (Array.isArray(aliases)) {
    for (const key of aliases) {
      if (key) fileMap[key] = file;
    }
  }
  fileMap[resolvedUrl] = file;
  fileMap[resolvedNoSlash] = file;
  if (baseName) fileMap[baseName] = file;
}
